import { CellTable, Text } from "@/atoms"
import {
  getDefaultColumnParams,
  getOrderedColumnParams
} from "@/helpers/table.helpers"
import { AGENT_AI_DISCOVERY_DRAWER_ID } from "@/organisms/Drawers/const"
import { MoreTableCellDrawer } from "@/pages/DiscoveryPage/components/molecules/MoreTableCellDrawer"
import { AiDecisionOutcomeTag } from "./AiDecisionOutcomeTag"
import { AiPolicyActionTag } from "./AiPolicyActionTag"
import { WorkloadDecisionCell } from "./WorkloadDecisionCell"

import styles from "./AiPolicyDecision.module.scss"

import type {
  AiPolicyAction,
  AiPolicyDecisionSubject,
  AiPolicyWorkloadAgent
} from "@/api/useAiPolicies.types"
import type { RowClick } from "@/hooks/useLinkDrawerTooltip"
import type { ORDER_BY_PROPS } from "@/hooks/useOrderBy"
import type { TFunction } from "@/hooks/useTranslation"
import type { AiDecisionOutcome } from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies.types"

// The agent as the cell's popup lists it: a link to its drawer, like an
// application's images
const agentItem = (name: string, drawerEntityId: string) => ({
  name,
  drawerId: AGENT_AI_DISCOVERY_DRAWER_ID,
  drawerEntityId
})

// A decision's columns, by name: the tables that list decisions pick theirs
// and set their widths. onRowClick opens the agent a decision names, by its
// inventory id. With a sorter, the columns the API orders decisions by sort,
// by the API's names; the action has no order on the API, so it never sorts
export const getDecisionColumns = (
  t: TFunction,
  onRowClick: RowClick,
  sorter?: Omit<ORDER_BY_PROPS, "onSorterChange">
) => {
  const tKey = "governance.ai.live.decisions"
  const agentCell = MoreTableCellDrawer(onRowClick)

  return {
    time: {
      title: t(`${tKey}.columns.time`),
      render: CellTable,
      ...getOrderedColumnParams("decided", sorter, "decidedAt")
    },
    platform: {
      title: t(`${tKey}.columns.platform`),
      render: CellTable,
      ...getOrderedColumnParams("platformLabel", sorter, "platform")
    },
    // What the decision is on: an agent, an MCP server policy's MCP server, or
    // a workload
    agent: {
      title: t(`${tKey}.columns.resource`),
      // A decision whose agent the platform did not report says so; one on a
      // known agent opens it on a click, like an application's images. An MCP
      // server is named only; a workload lists its agents
      render: (
        agent: string,
        record: {
          key: string
          agentInventoryId?: string | null
          mcpServerName?: string
          subject?: AiPolicyDecisionSubject
          workload?: string
          workloadAgents?: AiPolicyWorkloadAgent[]
        }
      ) => {
        const { agentInventoryId, mcpServerName } = record

        if (record.subject === "workload")
          return WorkloadDecisionCell(t, onRowClick, record)
        if (mcpServerName) return CellTable(mcpServerName)

        if (agent && agentInventoryId)
          return (
            <span
              className={styles.agentCell}
              onClick={(event) => {
                // A click in the popup reaches here through its portal: the
                // popup opens the agent itself
                if (!event.currentTarget.contains(event.target as Node)) return

                onRowClick(
                  { key: agentInventoryId },
                  AGENT_AI_DISCOVERY_DRAWER_ID
                )
              }}
            >
              {agentCell({
                id: `agent-${record.key}`,
                label: agent,
                // A count, even of none, keeps the name plain until hovered
                count: 0,
                items: [agentItem(agent, agentInventoryId)],
                copyable: true
              })}
            </span>
          )

        return agent ? (
          CellTable(agent)
        ) : (
          <Text type="secondary">{t(`${tKey}.notReported`)}</Text>
        )
      },
      ...getOrderedColumnParams("agent", sorter, "agentName")
    },
    tool: {
      title: t(`${tKey}.columns.tool`),
      render: CellTable,
      ...getOrderedColumnParams("tool", sorter)
    },
    policy: {
      title: t(`${tKey}.columns.policy`),
      render: CellTable,
      ...getOrderedColumnParams("policyName", sorter, "policy")
    },
    action: {
      title: t(`${tKey}.columns.action`),
      render: (action: AiPolicyAction | null) =>
        action ? <AiPolicyActionTag action={action} /> : null,
      ...getDefaultColumnParams("action")
    },
    outcome: {
      title: t(`${tKey}.columns.outcome`),
      render: (outcome: AiDecisionOutcome) => (
        <AiDecisionOutcomeTag outcome={outcome} />
      ),
      ...getOrderedColumnParams("outcome", sorter)
    },
    enforcedBy: {
      title: t(`${tKey}.columns.enforcedBy`),
      render: CellTable,
      ...getOrderedColumnParams("enforcedBy", sorter)
    }
  }
}
